import { useEffect, useState } from "react";
import { log } from "../utils/logger";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import { chapters } from "../data/chapters";
import Navbar from "../components/Navbar";
import axios from "axios";
import {
  ArrowRight,
  CheckCircle2,
  FileText,
  UserCheck,
  FilePlus,
  BookOpen,
  Hash,
  Landmark,
  Receipt,
  Shield,
  CalendarCheck,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const iconMap = {
  FileText, UserCheck, FilePlus, BookOpen, Hash, Landmark, Receipt, Shield, CalendarCheck,
};

export default function QuickStartPage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [completedSteps, setCompletedSteps] = useState([]);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!loading && !user) navigate("/login");
  }, [loading, user, navigate]);

  useEffect(() => {
    if (!user) return;
    const load = async () => {
      try {
        const { data } = await axios.get(`${API}/progress`, { withCredentials: true });
        if (data.completed_steps) setCompletedSteps(data.completed_steps);
      } catch (err) {
        log.warn("Failed to load progress", err);
      }
      setReady(true);
    };
    load();
  }, [user]);

  if (loading || !user || !ready) return null;

  // Build checklist index (immutable)
  const chapterChecklistStart = chapters.map((_, i) =>
    chapters.slice(0, i).reduce((sum, c) => sum + c.checklistItems.length, 0)
  );

  const totalChecklist = chapters.reduce((a, c) => a + c.checklistItems.length, 0);
  const completedTotal = completedSteps.length;
  const progressPercent = totalChecklist > 0 ? Math.round((completedTotal / totalChecklist) * 100) : 0;

  // Find the current step (first incomplete chapter)
  const currentStepIndex = chapters.findIndex((ch, i) => {
    const startIdx = chapterChecklistStart[i];
    return ch.checklistItems.some((_, j) => !completedSteps.includes(startIdx + j));
  });
  const currentStep = currentStepIndex >= 0 ? chapters[currentStepIndex] : null;

  return (
    <div className="flex min-h-screen flex-col bg-[var(--c-bg)]">
      <Navbar />

      <div className="mx-auto w-full max-w-4xl px-4 py-8 sm:px-6 sm:py-12">
        {/* Hero */}
        <div className="mb-12 text-center">
          <div className="mx-auto mb-5 flex h-24 items-center justify-center overflow-hidden rounded-2xl border-2 border-[var(--c-border)] bg-white px-5 shadow-[4px_4px_0px_var(--c-shadow)]">
            <img src="/logo.png" alt="Texas LLC Guide Logo" className="h-20 w-auto object-contain" />
          </div>
          <h1
            data-testid="quickstart-title"
            className="font-heading text-4xl font-black tracking-tight text-[var(--c-text)] sm:text-5xl"
          >
            Your LLC Roadmap
          </h1>
          <p className="mx-auto mt-3 max-w-xl text-base text-[var(--c-muted)] sm:text-lg">
            9 steps from idea to legally formed Texas LLC. Follow the roadmap, check off tasks, and launch your business with confidence.
          </p>

          {/* Overall progress */}
          {completedTotal > 0 && (
            <div className="mx-auto mt-6 max-w-sm rounded-xl border-2 border-[var(--c-border)] bg-[var(--c-surface)] p-4 shadow-[3px_3px_0px_var(--c-shadow)]">
              <div className="flex items-center justify-between text-xs font-bold text-[var(--c-muted)]">
                <span>Your Progress</span>
                <span>{progressPercent}%</span>
              </div>
              <div className="mt-2 h-2.5 overflow-hidden rounded-full border border-[var(--c-border)] bg-[var(--c-input-bg)]">
                <div
                  className="h-full rounded-full bg-[#1E3A8A] transition-all"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
              <p className="mt-1.5 text-xs text-[var(--c-muted)]">
                {completedTotal} of {totalChecklist} tasks completed
              </p>
            </div>
          )}
        </div>

        {/* Resume Banner - shows for returning users with partial progress */}
        {completedTotal > 0 && currentStep && (
          <div
            data-testid="resume-banner"
            className="mb-8 overflow-hidden rounded-xl border-2 border-[#1E3A8A] bg-[#1E3A8A]/5 shadow-[4px_4px_0px_var(--c-shadow)]"
          >
            <div className="flex items-center justify-between gap-4 p-5">
              <div className="flex items-center gap-4">
                <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl border-2 border-[#1E3A8A] bg-[#1E3A8A] text-lg font-bold text-white">
                  {currentStep.id}
                </div>
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.15em] text-[#1E3A8A]">Pick up where you left off</p>
                  <p className="mt-0.5 font-heading text-base font-bold text-[var(--c-text)] sm:text-lg">
                    Step {currentStep.id}: {currentStep.title}
                  </p>
                </div>
              </div>
              <button
                data-testid="resume-button"
                onClick={() => navigate("/ebook", { state: { page: currentStepIndex } })}
                className="flex flex-shrink-0 items-center gap-2 rounded-full border-2 border-[#1E3A8A] bg-[#1E3A8A] px-5 py-2.5 font-bold text-white shadow-[3px_3px_0px_var(--c-shadow)] transition-all hover:-translate-y-0.5 hover:shadow-[4px_4px_0px_var(--c-shadow)]"
              >
                Continue
                <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}

        {/* Timeline */}
        <div data-testid="roadmap-timeline" className="relative">
          {/* Vertical line */}
          <div className="absolute left-6 top-0 hidden h-full w-0.5 bg-[var(--c-border-light)] sm:block" />

          <div className="space-y-4">
            {chapters.map((ch, i) => {
              const Icon = iconMap[ch.icon] || FileText;
              const startIdx = chapterChecklistStart[i];
              const chCompleted = ch.checklistItems.filter(
                (_, j) => completedSteps.includes(startIdx + j)
              ).length;
              const chTotal = ch.checklistItems.length;
              const allDone = chCompleted === chTotal;
              const hasProgress = chCompleted > 0;

              return (
                <button
                  key={ch.id}
                  data-testid={`roadmap-step-${ch.id}`}
                  onClick={() => navigate("/ebook", { state: { page: i } })}
                  className="group flex w-full items-start gap-4 rounded-xl border-2 border-[var(--c-border)] bg-[var(--c-surface)] p-5 text-left shadow-[3px_3px_0px_var(--c-shadow)] transition-all hover:-translate-y-0.5 hover:shadow-[5px_5px_0px_var(--c-shadow)] sm:pl-16"
                >
                  {/* Step number circle - positioned on timeline for desktop */}
                  <div
                    className={`relative z-10 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl border-2 text-sm font-bold sm:absolute sm:left-1 ${
                      allDone
                        ? "border-[var(--c-border)] bg-[#A3E6D5] text-[#1A1918]"
                        : hasProgress
                        ? "border-[var(--c-border)] bg-[#1E3A8A] text-white"
                        : "border-[var(--c-border-light)] bg-[var(--c-surface)] text-[var(--c-muted)]"
                    }`}
                  >
                    {allDone ? <CheckCircle2 className="h-5 w-5" /> : ch.id}
                  </div>

                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <Icon className="h-4 w-4 text-[#1E3A8A]" />
                      <h3 className="font-heading text-base font-bold text-[var(--c-text)] sm:text-lg">
                        {ch.title}
                      </h3>
                    </div>
                    <p className="mt-1 text-sm text-[var(--c-muted)]">{ch.subtitle}</p>

                    {/* Mini progress */}
                    <div className="mt-3 flex items-center gap-3">
                      <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-[var(--c-input-bg)]">
                        <div
                          className={`h-full rounded-full transition-all ${allDone ? "bg-[#A3E6D5]" : "bg-[#1E3A8A]"}`}
                          style={{ width: `${chTotal > 0 ? (chCompleted / chTotal) * 100 : 0}%` }}
                        />
                      </div>
                      <span className="text-xs font-bold text-[var(--c-muted)]">
                        {chCompleted}/{chTotal}
                      </span>
                    </div>
                  </div>

                  <ArrowRight className="mt-1 h-5 w-5 flex-shrink-0 text-[var(--c-muted)] transition-transform group-hover:translate-x-1" />
                </button>
              );
            })}
          </div>
        </div>

        {/* CTA */}
        <div className="mt-10 text-center">
          <button
            data-testid="start-reading-button"
            onClick={() => navigate("/ebook")}
            className="inline-flex items-center gap-2 rounded-full border-2 border-[var(--c-border)] bg-[#1E3A8A] px-8 py-4 text-lg font-bold text-white shadow-[4px_4px_0px_var(--c-shadow)] transition-all hover:-translate-y-1 hover:shadow-[6px_6px_0px_var(--c-shadow)]"
          >
            {completedTotal > 0 ? "Continue Reading" : "Start Reading"}
            <ArrowRight className="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  );
}
